module App

open Elmish
open Elmish.Navigation
open Client
open Client.Shared
open Client.Pages
open Thoth.Fetch
open Thoth.Json
open Fetch.Types
/// The navigation logic of the application given a page identity parsed from the .../#info
/// information in the URL.
let urlUpdate (result: Option<Page>) (model : Model) =
  Logger.trackPageViewWithDetails {| OriginatingPage = model.route; DestinationPage = result; |}

  let newModel, newCmd =
    match result with
    // Pages that do not require auth
    | Some Page.MeterReadings -> model.setRoute Page.MeterReadings, Cmd.none
    | Some Page.ServiceCalls -> model.setRoute Page.ServiceCalls, Cmd.none
    | Some Page.Supplies -> model.setRoute Page.Supplies, Cmd.none
    | Some (Page.EquipmentLookup equipmentNumber) -> model.setRoute (Page.EquipmentLookup equipmentNumber), Cmd.none
    | Some Page.Equipment -> model.setRoute Page.Equipment, Cmd.none
    | Some (Page.Location locationId) -> model.setRoute (Page.Location locationId), Cmd.none
    | Some Page.Locations -> model.setRoute Page.Locations, Cmd.none
    | Some (Page.LocationEquipment (locationId, equipmentId)) -> model.setRoute (Page.LocationEquipment (locationId, equipmentId)), Cmd.none
    | Some (Page.LocationEquipments locationId) -> model.setRoute (Page.LocationEquipments locationId), Cmd.none
    | Some (Page.LocationInvoice (locationId, invoiceId)) -> model.setRoute (Page.LocationInvoice (locationId, invoiceId)), Cmd.none
    | Some (Page.LocationInvoices locationId) -> model.setRoute (Page.LocationInvoices locationId), Cmd.none
    | Some Page.Home -> model.setRoute Page.Home, Cmd.none
    | Some Page.Login -> model.setRoute Page.Login, Cmd.none
    // Default for no matching route
    | None -> model.setRoute Page.Home, Navigation.modifyUrl (Pages.toHash Pages.Page.Home)

  newModel, newCmd

let init (options : Cmd.Options) route : Model*Cmd<Msg> =
    let token = Browser.Dom.window.localStorage.getItem "token"
    let expires = Browser.Dom.window.localStorage.getItem "expires"
    let username = Browser.Dom.window.localStorage.getItem "username"
    let linkedCustomersCount = Browser.Dom.window.localStorage.getItem"linkedcustomerscount"
    let userOption =
        if not (isNull token || isNull expires || isNull username || isNull linkedCustomersCount) then
          let linkedCustomers =
            match System.Int32.TryParse linkedCustomersCount with
            | true, count when count >= 0 -> Some count
            | _ -> None
          let exp =
            match System.Int64.TryParse expires with
            | true,exp when exp > System.DateTime.UtcNow.Ticks -> Some exp
            | _ -> None
          match linkedCustomers, exp with
          | Some count, Some exp ->
            let tokenRequestFn () =
                promise {
                    return token
                }
            Client.Services.accessTokenFunction <- tokenRequestFn
            Some { Username = username; Expires = exp; LinkedCustomersCount = count; }
          | _ ->
            None
        else
          None
    // Browser.Dom.console.log($"User option: %A{userOption}")
    // Browser.Dom.console.log($"Route: %A{route}")
    match route, userOption with
    | Some Pages.Page.Login, Some user -> Model.LoggedIn { LoggedInModel.route = Pages.Page.Login; user = user; reCaptchaSiteKey = options.recaptchaSiteKey; }, Navigation.modifyUrl (Pages.toHash Pages.Page.Locations)
    | Some r, Some user -> Model.LoggedIn { LoggedInModel.route = r; user = user; reCaptchaSiteKey = options.recaptchaSiteKey; }, Navigation.modifyUrl (Pages.toHash r)
    | Some r, None -> Model.LoggedOut { LoggedOutModel.route = r; reCaptchaSiteKey = options.recaptchaSiteKey; }, Navigation.modifyUrl (Pages.toHash r)
    | None, Some user -> Model.LoggedIn { LoggedInModel.route = Page.Home; user = user; reCaptchaSiteKey = options.recaptchaSiteKey; }, Navigation.modifyUrl (Pages.toHash Pages.Page.Home)
    | None, None -> Model.LoggedOut { LoggedOutModel.route = Page.Home; reCaptchaSiteKey = options.recaptchaSiteKey; }, Navigation.modifyUrl (Pages.toHash Pages.Page.Home)

let update (msg: Msg) (model: Model) =
  match model, msg with
  | _, LoginSuccess u ->
    match u.LoginType with
    | Shared.LoginType.StandardLogin ->
      Browser.Dom.window.localStorage.setItem("token", u.Token)
      Browser.Dom.window.localStorage.setItem("expires", $"%i{u.Expires}")
      Browser.Dom.window.localStorage.setItem("username", u.Username)
      Browser.Dom.window.localStorage.setItem("linkedcustomerscount", $"%i{u.LinkedCustomersCount}")
    | Shared.LoginType.TempLogin -> ()
    let tokenRequestFn () =
        promise {
            return u.Token
        }
    Client.Services.accessTokenFunction <- tokenRequestFn
    let user = { Username = u.Username; Expires = u.Expires; LinkedCustomersCount = u.LinkedCustomersCount; }
    match u.LoginType with
    | Shared.LoginType.TempLogin -> Model.TempLoggedIn { TempLoggedInModel.route = model.route; user = user; reCaptchaSiteKey = model.reCaptchaSiteKey; }, Cmd.none
    | Shared.LoginType.StandardLogin -> Model.LoggedIn { LoggedInModel.route = model.route; user = user; reCaptchaSiteKey = model.reCaptchaSiteKey; }, Cmd.none
  | _, LoginRefreshSuccess u ->
    match model with
    | LoggedIn loggedInModel ->
      Browser.Dom.window.localStorage.setItem("token", u.Token)
      Browser.Dom.window.localStorage.setItem("expires", $"%i{u.Expires}")
      Browser.Dom.window.localStorage.setItem("username", u.Username)
      let tokenRequestFn () =
          promise {
              return u.Token
          }
      Client.Services.accessTokenFunction <- tokenRequestFn
      LoggedIn { loggedInModel with user = { loggedInModel.user with Expires = u.Expires; } }, Cmd.none
    | TempLoggedIn tempLoggedInModel ->
      model, Cmd.none
    | LoggedOut _ ->
      model, Cmd.none
  | _, Logout ->
    Browser.Dom.window.localStorage.clear()
    Model.LoggedOut { LoggedOutModel.route = model.route; reCaptchaSiteKey = model.reCaptchaSiteKey }, Cmd.none
  | _, Failed e ->
      model, Cmd.none
  | _,CheckTokenValidity ->
    let ticksToAllow = System.TimeSpan.FromMinutes(5).Ticks
    match model with
    | LoggedIn loggedInModel when loggedInModel.user.Expires <= System.DateTime.UtcNow.Ticks ->
      Model.LoggedOut { LoggedOutModel.route = model.route; reCaptchaSiteKey = model.reCaptchaSiteKey }, Cmd.none
    | LoggedIn loggedInModel when (System.DateTime.UtcNow.Ticks - loggedInModel.user.Expires) > ticksToAllow ->
      model, Cmd.ofMsg RefreshToken
    | _ -> model, Cmd.none
  | _,RefreshToken ->
    match model with
    | LoggedIn loggedInModel ->
      let cmd =
        Cmd.OfPromise.either
          (fun () ->
            promise {
              let! authHeader = Services.getAuthHeader()
              let! result = Fetch.get("/api/Login/Refresh", headers = [ authHeader ])
              return result
            }
          )
          ()
          (fun res -> LoginRefreshSuccess res)
          (fun _ -> Logout)
      model, cmd
    | _ -> model, Cmd.none
  | _, PasswordChanged ->
    model, Cmd.ofMsg Logout
  // | GoTo of Pages.Page
  | _ -> model, Cmd.none

open Elmish.React

#if DEBUG
open Elmish.Debug
open Elmish.HMR
#endif

let timer initial =
    let sub dispatch =
        Fable.Core.JS.setInterval
            (fun _ -> dispatch (CheckTokenValidity))
            60_000 // every 60 seconds
            |> ignore
    Cmd.ofSub sub

promise {
    let! options = Cmd.ofConfig "/options.json"
    Program.mkProgram (init options) (update) View.root
    |> Program.withSubscription timer
    |> Program.toNavigable (UrlParser.parseHash Pages.pageParser) urlUpdate
    #if DEBUG
    |> Program.withConsoleTrace
    #endif
    |> Program.withReactBatched "elmish-app"
    #if DEBUG
    |> Program.withDebugger
    #endif
    |> Program.run
}
|> ignore